import { routes } from 'defs';
import * as React from 'react';

import Spacer from 'components/helper/Spacer';
import { ButtonBarProps } from 'components/molecules/ButtonBar';
import ButtonBar from 'components/molecules/ButtonBar';
import { getHealthGoalDisplayStatus } from 'components/molecules/HealthGoal/get-display-status';
import { TGoal } from 'components/molecules/HealthGoalSelection/HealthGoalSelection';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';
import { actionCreators as profileAC } from 'store/profile/profile.actionCreators';
import { ArgumentTypes } from 'utils/types';
import { AllergyType, DietaryHabitsItem, NutritionPhilosophyType } from '../../../../types/DietaryHabits';
import { Choice, ChoiceStateValue } from '../../../molecules/Choice/Choice';
import { getHealthGoalRequiredAgreement } from '../get-health-goal-required-agreement';

export type DietaryHabitsProps = {
	allergiesTitle: string;
	nutritionPhilosophyListTitle: string;
	nutritionPhilosophy: NutritionPhilosophyType | null;
	allergies: AllergyType[] | null;
	nutritionPhilosophyList: DietaryHabitsItem<NutritionPhilosophyType>[];
	allergyList: DietaryHabitsItem<AllergyType>[];
	actionButtonsActive: ButtonBarProps;
	isUpdatingAnamnesis: boolean;
	onNavigationClick: (targetUrl: { href: string }) => void;
	onStepCompleted: (resolver: AnamnesisStepResolver<ArgumentTypes<typeof profileAC.doSaveDietaryHabits>[0]>) => void;
	doSaveDietaryHabits?: typeof profileAC.doSaveDietaryHabits;
	referrer?: string;
	bmiValue: number;
	size?: number;
	weight?: number;
	isPregnant?: boolean;
	selectedGoal?: TGoal;
};

export type DietaryHabitsState = {
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
};

export default class DietaryHabits extends React.Component<DietaryHabitsProps, DietaryHabitsState> {
	public static displayName = 'AnamnesisDietaryHabits';
	public static defaultProps = {
		nutritionPhilosophy: 'UNKNOWN',
		allergies: ['UNKNOWN'],
	};

	constructor(props: DietaryHabitsProps) {
		super(props);

		this.state = {
			nutritionPhilosophy: props.nutritionPhilosophy || 'UNKNOWN',
			allergies: props.allergies !== null && props.allergies.length !== 0 ? props.allergies : ['UNKNOWN'],
		};
	}

	public onHandleButtonClicks = (type: string) => {
		const _this = this;
		const { onNavigationClick, onStepCompleted, isPregnant, selectedGoal, bmiValue, weight, size } = this.props;

		if (type === 'completed' && typeof onStepCompleted === 'function') {
			return onStepCompleted.apply(null, [
				{
					actionCreator: _this.props.doSaveDietaryHabits,
					waitFor: 'savedDietaryHabits',
					nextRoute: routes.anamnesis.selectActivityLevel,
					data: {
						nutritionPhilosophy: this.state.nutritionPhilosophy,
						allergies: this.state.allergies,
					},
				},
			]);
		}

		// Go back
		if (typeof onNavigationClick === 'function') {
			type = type === 'completed' ? 'next' : type;

			const requiredArgreement = getHealthGoalRequiredAgreement({
				isPregnant,
				selectedGoal,
				weightInput: bmiValue,
				weight,
				size,
				bmiValue,
			});
			const displayStatus = getHealthGoalDisplayStatus({
				isPregnant,
				bmiValue,
				selectedGoal,
			});

			// step accept health target not needed PULSWM-3817
			if (!requiredArgreement && !displayStatus) {
				onNavigationClick.apply(this, [routes.anamnesis.defineHealthTarget]);
				return;
			}

			onNavigationClick.apply(this, [routes.anamnesis.acceptHealthTarget]);
		}
	};

	public renderButtons() {
		const ActiveButtonBar = (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsActive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);

		return ActiveButtonBar;
	}

	public handleChoiceChange = (choiceId: NutritionPhilosophyType | AllergyType) => {
		const { nutritionPhilosophyList, allergyList } = this.props;

		if (nutritionPhilosophyList.find((item) => item.id === choiceId)) {
			this.handleNutritionPhilosophyChange(choiceId as NutritionPhilosophyType);
		} else if (allergyList.find((item) => item.id === choiceId)) {
			this.handleAllergyChange(choiceId as AllergyType);
		}
	};

	public handleNutritionPhilosophyChange(id: NutritionPhilosophyType) {
		const alreadySelected = this.state.nutritionPhilosophy === id;
		let nutritionPhilosophy = id;

		// select MEAT_FISH as default
		if (alreadySelected) {
			nutritionPhilosophy = 'MEAT_FISH';
		}

		if (nutritionPhilosophy !== this.state.nutritionPhilosophy) {
			this.setState({
				nutritionPhilosophy,
			});
		}
	}
	public handleAllergyChange(id: AllergyType) {
		if (id === 'NONE') {
			// remove other allergies from selection
			this.setState({
				allergies: [id],
			});
		} else {
			const alreadySelected = this.state.allergies.indexOf(id) !== -1;
			if (alreadySelected) {
				// deselect allergy
				let allergies = this.state.allergies.filter((a) => a !== id);
				// select NONE if it was the last one selected
				if (allergies.length === 0) {
					allergies = [...allergies, 'NONE'];
				}
				this.setState({ allergies });
			} else {
				// remove 'NONE' and 'UNKNOWN' from selection when selecting an allergy
				const allergiesToCleanUp = ['NONE', 'UNKNOWN'];
				const allergiesCleanedUp = this.state.allergies.filter((a) => !allergiesToCleanUp.includes(a));
				this.setState({
					allergies: [...allergiesCleanedUp, id],
				});
			}
		}
	}

	public render() {
		const { allergiesTitle, nutritionPhilosophyListTitle, nutritionPhilosophyList, allergyList } = this.props;

		return (
			<div>
				<h2 className="o-anamnesis__title">{nutritionPhilosophyListTitle}</h2>
				<Choice
					choices={nutritionPhilosophyList.map((item) => ({
						...item,
						state: this.state.nutritionPhilosophy === item.id ? ('selected' as ChoiceStateValue) : '',
						deselectable: item.id === 'MEAT_FISH' ? false : true,
					}))}
					imagePath="/img/svg/anamnesis/"
					handleChoiceChange={this.handleChoiceChange}
				/>
				<h2 className="o-anamnesis__title">{allergiesTitle}</h2>
				<Choice
					choices={allergyList.map((item) => ({
						...item,
						state: this.state.allergies.includes(item.id as AllergyType)
							? ('selected' as ChoiceStateValue)
							: '',
						deselectable: item.id === 'NONE' ? false : true,
					}))}
					imagePath="/img/svg/anamnesis/"
					handleChoiceChange={this.handleChoiceChange}
					multiple
				/>
				<Spacer size="m" />
				{this.renderButtons()}
			</div>
		);
	}
}
